import { BORDER_RADIUS, COLORS, BREAKPOINT, TYPE_SIZE } from "utils/styles";

import { FullWidthContainer } from "components/PageContainer";
import Head from "next/head";
import Nav from "components/Nav/Nav";
import React from "react";
import styled from "styled-components";

import SecondaryNav from "components/Nav/SecondaryNav";
import FlexSpacer from "components/FlexSpacer";
import {
  SecondaryMenuWrapper,
  SecondaryMenuButton,
  SecondaryMenuGroup,
} from "components/Nav/components";

import Section from "components/Section";
import Faq from "components/faq";

import Link from "next/link";

const Container = styled.div`
  display: flex;
  margin-bottom: 8rem;
  margin-top: 8rem;
  @media ${BREAKPOINT.M} {
    text-align: left;
    margin-bottom: 2rem;
    margin-top: 3rem;
    margin: auto;
    padding: 5rem 1rem;
  }
`;

const ContentWrapper = styled.div`
  color: ${COLORS.DARKEST_GRAY};
  max-width: 800px;
  margin: auto;
`;

const SectionWrapper = styled.div`
  margin-bottom: 10rem;
`;

const Heading = styled.h1.attrs({ className: "mabry-regular" })`
  font-size: 4rem;
  margin-top: 1em;
  margin-bottom: 1em;

  @media ${BREAKPOINT.M} {
    font-size: 2rem;
    padding-left: 0.5rem;
  }
`;

const Subheading = styled.h1.attrs({ className: "mabry-regular" })`
  font-size: 2rem;
  margin-top: 1em;
  margin-bottom: 1em;

  @media ${BREAKPOINT.M} {
    font-size: 1.25rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    margin-top: 2em;
  }
`;

const P = styled.p`
  font-size: ${TYPE_SIZE.M};

  max-width: 45em;

  @media ${BREAKPOINT.M} {
    font-size: 1rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  & a {
    color: inherit;
    text-decoration: underline;
  }
`;
const UL = styled.ul`
  max-width: 45em;
  margin-top: -2em;
  @media ${BREAKPOINT.M} {
    margin-top: -1em;
  }
`;
const LI = styled.li`
  font-size: 1.5rem;

  @media ${BREAKPOINT.M} {
    font-size: 1rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  & a {
    color: ${COLORS.BLACK};
    text-decoration: underline;

    &:hover,
    &:focus {
      color: ${COLORS.BLACK};
      text-decoration: none;
    }
  }
`;

const InPageNav = styled.nav`
  ${BORDER_RADIUS};
  padding: 1rem;
  position: sticky;
  width: 240px;
  top: 5rem;
  margin-right: 3rem;
  background: ${COLORS.LIGHTEST_GRAY};
`;

const InPageNavWrapper = styled.div`
  @media (max-width: 50em) {
    display: none;
  }
`;

const NavTitle = styled.span.attrs({ className: "mabry-regular" })`
  display: block;
  font-size: 1.125rem;
  margin-bottom: 1rem;
`;

const NavItemList = styled.ul`
  & li {
  }

  & a {
    color: inherit;
  }
`;

const MailchimpWrapper = styled.div`
  margin-left: -20px;
`;

export async function getStaticProps() {
  return {
    props: {
      baseUrl: process.env.BASE_URL,
    },
  };
}

export default function About(props) {
  const { baseUrl } = props;

  return (
    <div>
      <FullWidthContainer>
        <Head>
          <title>Narrative Observatory | About</title>
          <link rel="icon" href="/favcon2.png" />
          <meta
            property="og:image"
            content={`${baseUrl}/images/open-graph.png`}
          />
          <meta
            property="twitter:image"
            content={`${baseUrl}/images/open-graph.png`}
          />
        </Head>
        <main>
          <Nav
            breadCrumbs={[<a href="/about">About</a>]}
            secondaryNav={
              <SecondaryMenuWrapper>
                <FlexSpacer />
                <SecondaryMenuGroup>
                  <SecondaryMenuButton href="#background" text="Background" />
                  <SecondaryMenuButton href="#methods" text="Methods" />
                  <SecondaryMenuButton
                    href="#funders-partners-friends"
                    text="Partners"
                  />
                  <SecondaryMenuButton
                    href="#about-harmony-labs"
                    text="Harmony Labs"
                  />
                  <SecondaryMenuButton href="#faq" text="FAQ" />
                </SecondaryMenuGroup>
              </SecondaryMenuWrapper>
            }
          />
          <Container>
            <ContentWrapper>
              <Section id="background" align="left">
                <h1>Background</h1>
                <p>
                  Many funders and storytellers work to shift the cultural
                  narratives that condition public understanding and constrain
                  institutional action on issues like racial justice,
                  immigration, or climate. Sometimes this work lacks a shared
                  understanding of what narrative is, how to identify and
                  characterize it reliably, and how it connects to people with
                  the power to make change.
                </p>

                <p>
                  Harmony Labs’{" "}
                  <a
                    href="https://harmonylabs.org/narrative-observatory"
                    target="blank"
                  >
                    Narrative Observatory
                  </a>{" "}
                  aims to solve some of the technical, definitional, and
                  practical challenges bedeviling narrative and cultural
                  strategy. The Narrative Observatory provides narrative and
                  cultural strategists tools, like this website, along with
                  industry-grade data infrastructure to understand audiences
                  relative to their place in culture; to identify, measure, and
                  track narratives within audiences over long time scales; and
                  to surface audience-specific story opportunities and threats.
                </p>
                <p>
                  With funding from Bill & Melinda Gates Foundation, the
                  Narrative Observatory’s first iteration has focused on poverty
                  and economic mobility in the U.S. We are in the process of
                  expanding this work to additional issue and partners and would
                  like to have a conversation with you about supporting your
                  work.{" "}
                </p>
                <p>
                  <a href="mailto:info@harmonylabs.org">Email us</a>
                </p>
              </Section>
              <Section id="methods" align="left">
                <h1>Methods & Data</h1>
                <p>
                  As with all the tools we create, this website strives to
                  present as simply as possible only what is useful. Keeping our
                  findings short and sweet is really hard for us, because we’re
                  data nerds. Behind everything you see here is some fancy data
                  dancing, which we’re eager to share.{" "}
                </p>
                <p>
                  To create our audience classification, we started by reviewing
                  existing surveys (like{" "}
                  <a
                    href="https://onedrive.live.com/?authkey=%21ACSPUeSyVAEdByE&cid=624A7F93617FBDB7&id=624A7F93617FBDB7%21147&parId=624A7F93617FBDB7%21129&o=OneUp"
                    target="blank"
                  >
                    this one from GOOD
                  </a>
                  ) and reading media related to poverty, economic mobility,
                  COVID, Black Lives Matter, and other major events in 2020.
                  This helped inform survey questions exploring attitudes on
                  race, gender, place, and class, along with core values. Using
                  these questions, we conducted a 2,900 respondent, voter-file
                  matched survey and analyzed results to yield four audiences
                  and generate an 8-question audience classifier.{" "}
                </p>
                <p>
                  We relied on demographic details from the survey (e.g., age,
                  gender, race, zip code) to create predictive models and
                  project these audiences onto nationally representative, opt-in
                  media consumption panels. These media consumption panels give
                  us visibility into the minute-by-minute media behaviors—of
                  over 300,000 people in the U.S., who opted in and are
                  compensated for their participation—across desktop, mobile,
                  tablet, and TV. This allowed us to enrich our audience
                  profiles with the media artifacts—videos, news articles, TV
                  shows, Tweets, Pinterest pins—that actual audience members
                  actually engaged with.{" "}
                </p>
                <p>
                  Next, we worked to extract from all the media audiences
                  engaged with only artifacts relevant to poverty and economic
                  mobility. We did this using keywords and human annotation to
                  build supervised relevance models for each media type. We
                  considered a media artifact poverty-relevant, if it told us
                  what people experiencing poverty or financial wellbeing are
                  like (e.g., hard-working, virtuous, impure); what it is or
                  feels like to experience poverty or financial wellbeing; or
                  how people go from poverty to financial wellbeing or vice
                  versa.{" "}
                </p>
                <p>
                  Then, our human analysts—representing each of our
                  audiences—read a randomly generated poverty-relevant sample of
                  each media type, in order to extract key dimensions for story
                  pattern variations. At the same time, we used machines and
                  natural language processing to capture and cluster naturally
                  occurring story patterns. The outputs of both human and
                  machine analyses were used to generate a preliminary narrative
                  structure for news, TV, and Twitter. This structure was used
                  by another team of human annotators to code another sample of
                  poverty-relevant news articles, TV transcripts, and tweets. We
                  built supervised narrative models from these annotations,
                  which predict which narratives each article, tweet, or song is
                  associated with.{" "}
                </p>
                <p>
                  We subjected relevant media artifacts to an additional layer
                  of qualitative analysis to surface important story
                  opportunities, threats, and strategically important features,
                  which we share in this site’s{" "}
                  <Link href="/feed/" target="blank">
                    Stories & Opportunities
                  </Link>{" "}
                  section.
                </p>
                <p>
                  All the media data in the Narrative Observatory come from
                  commercial partners, detailed in the{" "}
                  <a href="#partners">Partners</a> section below, who donate
                  their data to this work. As a 501(c)3 organization, bound to
                  serve the public good, Harmony Labs has adopted a set of
                  principles and practices around data to: ensure we only
                  gather, use, and store data that supports our mission;
                  anonymize at ingest any data that contains personally
                  identifiable information; maintain robust security, limited
                  access, and encryption; and actively work with our partners
                  and in-house data science team to adhere to the highest
                  standards for scientific integrity, clearly communicating
                  methods, assumptions, and practices.{" "}
                </p>
                <p>
                  In this site’s charts and graphs, percentages may not add up
                  to 100 due to rounding.{" "}
                </p>
              </Section>

              <Section align="left">
                <h1 id="funders-partners-friends">
                  Funders, Partners, Friends
                </h1>
                <p>
                  Many thanks to everyone who makes this work possible. You’re
                  the best!
                </p>
                <h2>Funders</h2>
                <ul>
                  <li>
                    <a href="https://www.gatesfoundation.org/" target="blank">
                      Bill &amp; Melinda Gates Foundation
                    </a>
                  </li>
                  <li>The Atlantic Foundation</li>
                </ul>
                <h2>Partners</h2>
                <ul>
                  <li>
                    <a href="https://marvelous.ai/" target="blank">
                      Marvelous.ai
                    </a>
                  </li>
                  <li>
                    <a href="https://www.peakmetrics.com/" target="blank">
                      Peakmetrics
                    </a>
                  </li>
                  <li>
                    <a href="https://www.nielsen.com/us/en/" target="blank">
                      Nielsen
                    </a>
                  </li>
                  <li>
                    <a href="https://www.comscore.com/" target="blank">
                      Comscore
                    </a>
                  </li>
                  <li>
                    <a href="https://www.tveyes.com/" target="blank">
                      TVEyes
                    </a>
                  </li>
                </ul>
                <h2>Friends</h2>
                <ul>
                  <li>
                    <a href="https://civiqs.com/" target="blank">
                      Civiqs
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.frameworksinstitute.org/"
                      target="blank"
                    >
                      FrameWorks Institute
                    </a>
                  </li>
                  <li>
                    <a href="https://growprogress.ai/" target="blank">
                      GrowProgress
                    </a>
                  </li>
                  <li>
                    <a href="https://narrativeinitiative.org/" target="blank">
                      Narrative Initiative
                    </a>
                  </li>
                  <li>
                    <a href="https://learcenter.org/" target="blank">
                      The Norman Lear Center at USC Annenberg School
                    </a>
                  </li>
                  <li>
                    <a href="https://olsonzaltman.com/" target="blank">
                      Olson Zaltman
                    </a>
                  </li>
                  <li>
                    <a href="https://www.purpose.com/" target="blank">
                      Purpose Labs
                    </a>
                  </li>
                  <li>
                    <a href="https://today.yougov.com/" target="blank">
                      Yougov
                    </a>
                  </li>
                  <li>
                    <a href="https://www.psanches.com/" target="blank">
                      Pedro Sanches
                    </a>
                  </li>
                </ul>
              </Section>
              <Section align="left">
                <h1 id="about-harmony-labs">About Harmony Labs </h1>
                <p>
                  <a href="https://harmonylabs.org/" target="blank">
                    Harmony Labs
                  </a>{" "}
                  has been supporting narrative and cultural strategy for more
                  than a decade, helping storytellers channel the immense power
                  of story to shape the future.{" "}
                  <a
                    href="https://static1.squarespace.com/static/58595e5af5e23119f38fa37b/t/5884ee6a2e69cf6033c88910/1485106798222/NO_FRACKING_WAY_ASR.pdf"
                    target="blank"
                  >
                    One of the first papers we co-authored
                  </a>{" "}
                  looked at fracking narratives in documentary film. Since then,
                  we’ve worked on narratives for{" "}
                  <a
                    href="https://harmonylabs.org/project-ariel"
                    target="blank"
                  >
                    climate
                  </a>
                  ,{" "}
                  <a
                    href="https://medium.com/harmony-labs/anatomy-of-a-news-media-event-ba18f262385a"
                    target="blank"
                  >
                    gun violence
                  </a>
                  ,{" "}
                  <a
                    href="https://www.cjr.org/analysis/cable-news-trump-russia-ukraine.php"
                    target="blank"
                  >
                    political corruption
                  </a>
                  ,{" "}
                  <a
                    href="https://docsend.com/view/6wdaqqmmss9t56mt"
                    target="blank"
                  >
                    artificial intelligence
                  </a>
                  , reproductive rights, and other issues. With the{" "}
                  <a
                    href="https://harmonylabs.org/narrative-observatory"
                    target="blank"
                  >
                    Narrative Observatory
                  </a>
                  , for the first time ever, we’re harnessing powerful industry
                  relationships and an academic research network to develop data
                  infrastructure purpose-built for narrative identification and
                  tracking over long time scales, across media types.
                </p>
                <p>
                  Harmony Labs builds communities and tools to reform and
                  transform media systems. Our mission is to create a world
                  where media systems support democratic culture and healthy,
                  happy people.{" "}
                </p>
                <p>
                  <a href="mailto:info@harmonylabs.org">Email us</a> to start a
                  conversation. Follow us @harmonylabs on{" "}
                  <a href="https://twitter.com/harmonylabs" target="blank">
                    Twitter
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.linkedin.com/company/harmonylabs"
                    target="blank"
                  >
                    LinkedIn
                  </a>
                  , and{" "}
                  <a href="https://medium.com/harmony-labs" target="blank">
                    Medium
                  </a>
                  .
                </p>
              </Section>

              <Section align="left">
                <h1 id="faq">FAQ</h1>
                <p>
                  {" "}
                  Below are answers to some of the most common questions about
                  the Narrative Observatory. Please{" "}
                  <a href="mailto:hello@harmonylabs.org" target="blank">
                    reach out to us
                  </a>{" "}
                  with your questions.{" "}
                </p>
                <br></br>
                <br></br>
                <br></br>
                <Faq
                  question="Isn’t the Narrative Observatory just another social
                listening tool?"
                  answer={
                    <>
                      <p>
                        The Narrative Observatory differs from other social
                        listening tools insofar as it centers audience, the
                        actual lived media experience and behaviors of actual
                        people. At the core of the Narrative Observatory’s data
                        infrastructure are nationally representative audience
                        panels from Nielsen and Comscore that offer visibility
                        into what over 300,000 people in the U.S. are actually
                        doing online and on TV across desktop, mobile, and other
                        devices.
                      </p>
                      <p>
                        Putting these audience panels at the heart of what we do
                        means we’re not just counting tweets, hashtags, articles
                        produced, or bot traffic. Nor are we relying on
                        self-reporting from surveys to know which media to pay
                        attention to. We’re only counting and analyzing content
                        that actual people are actually engaging with. Which is
                        what sets us apart from many media listening tools on
                        the market, and a lot of narrative and media research
                        too.
                      </p>
                      <p>
                        To those audience panel data, we join terabytes of
                        content data: TV transcripts, online news articles, song
                        lyrics, YouTube video transcripts, and more. These data
                        allow us to look across media types for patterns in the
                        actual content people are choosing to consume. We use
                        machine learning models to help us with at scale content
                        analysis, of course, but the training data for these
                        models are always generated by actual audience members
                        reading and making judgements about content. Judgements
                        like: is this content about that issue? Or does this
                        content represent that narrative? This is a time
                        consuming, expensive process, but, through trial and
                        error, we’ve found it to be the only way to produce
                        meaningful, actionable, accurate results.
                      </p>
                    </>
                  }
                />

                <Faq
                  question="How does your work serve the public good?"
                  answer={
                    <p>
                      Media systems have evolved to be outrage machines, sorting
                      machines that are reproducing and reifying division and
                      difference, in ways that are starting to pose a real
                      threat to the conduct of civic life, in the U.S. and
                      elsewhere. We think of the Narrative Observatory as a tool
                      that helps people better navigate today’s media minefield
                      and, maybe over time, heal some of these real or perceived
                      divisions, making the work of advocacy much easier.
                      Specifically, we see this work as serving three
                      communities: content creators, advocacy organizations, and
                      philanthropic funders. People making content to support
                      cultural or narrative strategy often don’t have the time
                      or people power to do much more than a little anecdotal
                      desk research on audience, story, or narrative and are
                      often looking for off-the-shelf frameworks, insights, and
                      validated hypotheses that can help them do their work more
                      effectively. Advocacy organizations often have national
                      segmentations that capture how people relate to their
                      issue, but sometimes lack the nuanced cultural
                      understanding that can help them make or distribute strong
                      content. Philanthropic funders working on social issues at
                      scale are bound to be using media as part of their
                      strategy, and they need to know whether their media
                      investments are positively affecting public conversation.
                      We designed the Narrative Observatory to address these
                      needs.
                    </p>
                  }
                />

                <Faq
                  question="Where does data for the Narrative Observatory come from?"
                  answer={
                    <p>
                      All the media data in the Narrative Observatory come from
                      commercial partners, detailed in the Partners section
                      above, who donate their data to this work. This community
                      of data philanthropists includes big companies, like{" "}
                      <a href="https://www.nielsen.com/us/en/" target="blank">
                        Nielsen
                      </a>{" "}
                      and{" "}
                      <a href="https://www.comscore.com/" target="blank">
                        Comscore
                      </a>
                      , and also smaller startups, like{" "}
                      <a href="https://www.peakmetrics.com/" target="blank">
                        Peakmetrics
                      </a>
                      , that are scraping different corners of the web, news,
                      television, radio, and other kinds of media. We are
                      grateful to them for supporting the Narrative Observatory.
                      We also make these data available to a{" "}
                      <a
                        href="https://medium.com/harmony-labs/growing-our-data-growing-our-network-545f327f0187"
                        target="blank"
                      >
                        network of mostly academic researchers
                      </a>
                      , who use them to better understand how media systems
                      work, and how to improve them.
                    </p>
                  }
                />

                <Faq
                  question="What are your data practices with respect to privacy and
                security?"
                  answer={
                    <p>
                      As a 501(c)3 organization, bound to serve the public good,
                      Harmony Labs has adopted a set of principles and practices
                      around data to: ensure we only gather, use, and store data
                      that supports our mission; anonymize at ingest any data
                      that contains personally identifiable information;
                      maintain robust security, limited access, and encryption;
                      and actively work with our partners and in-house data
                      science team to adhere to the highest standards for
                      scientific integrity, clearly communicating methods,
                      assumptions, and practices. In general, we adhere to the
                      principle of least privilege. Which means that the Harmony
                      Labs’ team, partner, and technical infrastructure are only
                      given access to the resources and permissions necessary to
                      complete pre-specified goals. Even our applications and
                      supporting software are only able to interact with
                      specific services and data, limiting unintentional
                      cross-contamination and spillage vectors. Annual
                      independent security audits—most recently completed in
                      December 2020—validate our commitment to data security and
                      strong access controls.
                    </p>
                  }
                />

                <Faq
                  question="Is data from the Narrative Observatory open and accessible?"
                  answer={
                    <p>
                      Data donated to this project and to Harmony Labs are
                      available to participants in our{" "}
                      <a href="https://harmonylabs.org/research" target="blank">
                        research network
                      </a>
                      . The terms and conditions for data availability derive
                      from agreements with our data partners. We are always
                      happy to share our data and methods with others and have
                      tried our best to develop the Narrative Observatory in a
                      way that is open, accessible, and well documented on our{" "}
                      <a href="https://medium.com/harmony-labs" target="blank">
                        Medium page
                      </a>
                      .
                    </p>
                  }
                />
              </Section>
            </ContentWrapper>
          </Container>
        </main>
      </FullWidthContainer>
    </div>
  );
}
